import type { FC } from "react"
import React from "react"
import { Flex, Text, HStack, Image, Icon } from "@chakra-ui/react"
import { formatUnits } from "@ethersproject/units"

import type { Column } from "ui/components/Table"
import Table from "ui/components/Table"
import { useDevice } from "common/hooks/useDevice"
import Link from "common/components/Link"
import { chainIdToChainReference } from "web3/helpers/transformers"
import type { ChainId } from "query/graphql"
import ExternalLink from "ui/components/icons/ExternalLink"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import { TALLY_DEFAULT_LOGO_URL } from "common/constants/logo"
import { labelNumber } from "common/helpers/number"
import type { TokenBalance } from "common/types/tokenBalances"

type Props = {
  chainId?: ChainId
  tokens: TokenBalance[]
}

const AddressAssetsTable: FC<Props> = ({ tokens, chainId }) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  const chainReference = chainId ? chainIdToChainReference(chainId) : undefined

  const getBlockExplorerUrl = (token: TokenBalance) => {
    if (!chainReference) return null

    return token.tokenInfo.symbol === "ETH"
      ? EXTERNAL_ROUTES.etherscan.address(
          chainReference,
          token.tokenInfo.address,
        )
      : EXTERNAL_ROUTES.etherscan.token(chainReference, token.tokenInfo.address)
  }

  const generateLargeColumns = (tokens: TokenBalance[]): Column[] => [
    {
      title: "Asset",
      isNumeric: false,
      cells: tokens.map((token, index) => {
        const blockExplorerUrl = getBlockExplorerUrl(token)

        return (
          <HStack
            key={`address-asset-token-asset-${token.tokenInfo.name}-${index}`}
            spacing="1"
          >
            <Image
              alt="Asset image"
              fallbackSrc={TALLY_DEFAULT_LOGO_URL}
              h="8"
              mr={2}
              rounded="full"
              src={token.tokenInfo.logo}
              w="8"
            />
            {blockExplorerUrl ? (
              <Link isExternal href={blockExplorerUrl}>
                <HStack align="center" spacing={2}>
                  <Text color="gray.700" textStyle="body.bold.lg">
                    {token.tokenInfo.name}
                  </Text>
                  <Icon as={ExternalLink} h={3} w={3} />
                </HStack>
              </Link>
            ) : (
              <Text color="gray.700" textStyle="body.bold.lg">
                {token.tokenInfo.name}
              </Text>
            )}
          </HStack>
        )
      }),
    },
    {
      title: "Balance",
      isNumeric: false,
      cells: tokens.map((token) => {
        const tokenDecimals =
          token.tokenInfo.decimals !== "null"
            ? token.tokenInfo.decimals
            : undefined

        return (
          <HStack
            key={`address-asset-token-balance-${token.tokenInfo.name}`}
            spacing={1}
          >
            <Text color="gray.700" textStyle="body.bold.lg" w="fit-content">
              {labelNumber(
                Number(formatUnits(token.rawBalance, tokenDecimals)),
              )}
            </Text>
            <Text color="gray.500" textStyle="body.regular.lg" w="fit-content">
              {token.tokenInfo.symbol}
            </Text>
          </HStack>
        )
      }),
    },
    {
      title: "Value (24h change)",
      isNumeric: false,
      cells: tokens.map((token) => {
        const difference =
          Number(token.rawBalance) > 0
            ? Number(token.tokenInfo.price.rate) -
              Number(token.tokenInfo.price.rate24H)
            : 0

        const hasTokenDifference = Boolean(token.tokenInfo.price.rate24H)

        const rateDifference =
          Boolean(difference) && hasTokenDifference
            ? (difference / Number(token.tokenInfo.price.rate24H)) * 100
            : 0

        const percentageDifference = rateDifference.toFixed(2)
        const color = rateDifference < 0 ? "red.500" : "green.500"

        return (
          <HStack
            key={`address-asset-token-24h-${token.tokenInfo.price.rate}`}
            spacing={1}
          >
            <Text
              key={`address-asset-token-value-${token.tokenInfo.name}`}
              color="gray.700"
              textStyle="body.bold.lg"
              w="fit-content"
            >
              {labelNumber(Number(token.tokenInfo.fiat))}
              <Text
                as="span"
                color="gray.500"
                textStyle="body.regular.lg"
                w="fit-content"
              >
                {" "}
                USD
              </Text>
            </Text>
            {hasTokenDifference ? (
              <Text color={color} textStyle="body.regular.sm" w="fit-content">
                {percentageDifference}%
              </Text>
            ) : null}
          </HStack>
        )
      }),
    },
  ]

  const generateLittleColumns = (tokens: TokenBalance[]): Column[] => [
    {
      title: "Asset",
      isNumeric: false,
      cells: tokens.map((token, index) => (
        <HStack
          key={`address-asset-token-asset-${token.tokenInfo.name}-${index}`}
          spacing={2}
        >
          <Image
            alt="Asset image"
            fallbackSrc={TALLY_DEFAULT_LOGO_URL}
            h="8"
            rounded="full"
            src={token.tokenInfo.logo}
            w="8"
          />
          <Text color="gray.700" textStyle="body.bold.lg">
            {token.tokenInfo.symbol}
          </Text>
        </HStack>
      )),
    },
    {
      title: "Value",
      isNumeric: false,
      cells: tokens.map((token) => (
        <Text
          key={`address-asset-token-value-${token.tokenInfo.name}`}
          color="gray.700"
          textStyle="body.bold.lg"
          w="fit-content"
        >
          {labelNumber(Number(token.tokenInfo.fiat))}
          <Text
            as="span"
            color="gray.500"
            textStyle="body.regular.lg"
            w="fit-content"
          >
            {" "}
            USD
          </Text>
        </Text>
      )),
    },
  ]

  const largeColumns = generateLargeColumns(tokens)
  const littleColumns = generateLittleColumns(tokens)

  return (
    <Flex w="full">
      <Table
        columns={largeColumns}
        display={onLargeDevice}
        displayBorderTop={false}
        emptyMessage="There are no assets to show"
        id="governance-proposals"
      />
      <Table
        columns={littleColumns}
        display={onLittleDevice}
        displayBorderTop={false}
        emptyMessage="There are no assets to show"
        id="governance-proposals"
      />
    </Flex>
  )
}

export default AddressAssetsTable
